import { type FunctionComponent, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";
import { notEmpty } from "shared/lib/utils";

import type { videoShortCarousel$key } from "scmp-app/queries/__generated__/videoShortCarousel.graphql";

import { VideoShortCarouselContextProvider } from "./contexts";
import IconScmpShorts from "./icon-scmp-shorts.svg";
import { Container, NavButton, StyledSwiper, StyledSwiperSlide, Title } from "./styles";
import { VideoShortCarouselCard } from "./video-short-carousel-card";
import { VideoShortCarouselPlayerModal } from "./video-short-carousel-player-modal";

type Props = {
  className?: string;
  reference: videoShortCarousel$key;
  title?: string;
};

export const VideoShortCarousel: FunctionComponent<Props> = ({
  className,
  reference,
  title = "SCMP Shorts",
}) => {
  const queue = useFragment(
    graphql`
      fragment videoShortCarousel on Query @argumentDefinitions(queueName: { type: "String!" }) {
        queue(filter: { name: $queueName }) {
          items(first: 10) {
            edges {
              node {
                ...videoShortCarouselCard
              }
              ...videoShortCarouselPlayerModal
            }
          }
        }
      }
    `,
    reference,
  );

  const videos = useMemo(
    () => (queue?.queue?.items?.edges ?? []).filter(notEmpty),
    [queue?.queue?.items?.edges],
  );

  const isDesktop = useResponsive(theme.breakpoints.up("desktop"), true);

  if (videos.length < 7) return null;

  return (
    <VideoShortCarouselContextProvider>
      <Container className={className}>
        <Title>
          <IconScmpShorts />
          {title}
        </Title>
        <StyledSwiper
          cssMode={false}
          freeMode={true}
          navigation={{
            nextEl: ".swiper-button.next-button",
            prevEl: ".swiper-button.prev-button",
          }}
          pagination={false}
          slidesOffsetBefore={isDesktop ? 0 : 20}
          slidesPerGroupAuto={true}
          slidesPerView="auto"
        >
          {videos.map(({ node }, index) => (
            <StyledSwiperSlide key={index}>
              <VideoShortCarouselCard reference={node} slideId={index} />
            </StyledSwiperSlide>
          ))}
        </StyledSwiper>
        <NavButton className="swiper-button prev-button" />
        <NavButton $isNext className="swiper-button next-button" />
      </Container>
      <ClientSideSuspense>
        <VideoShortCarouselPlayerModal reference={videos} />
      </ClientSideSuspense>
    </VideoShortCarouselContextProvider>
  );
};

VideoShortCarousel.displayName = "VideoShortCarousel";
